import React, {
  ReactNode,
  SyntheticEvent,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { TOAST_TYPE, ToastContext } from "context/toastContext";
import { capitalize, formatPhoneNumber, startsWithNumber } from "utils";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { Contact } from "types";
import { SxProps } from "@mui/system";
import TextField from "@mui/material/TextField";
import { debounce } from "@mui/material/utils";
import { getTenantContacts } from "api";
import { useTranslation } from "react-i18next";
import { Stack, Typography } from "@mui/material";

export interface ContactAutocompleteProps {
  setContact: (contact: Contact | null) => void;
  variant?: "standard" | "outlined" | "filled";
  margin?: "none" | "dense" | "normal";
  size?: "small" | "medium";
  tenantID: number;
  disabled?: boolean;
  label?: string;
  sx?: SxProps;
}

const ContactAutocomplete = ({
  setContact,
  variant = "outlined",
  tenantID,
  disabled = false,
  label = "Search Contacts",
  sx,
  size = "medium",
  margin = "none",
}: ContactAutocompleteProps): ReactNode => {
  const { t } = useTranslation();
  const displayToast = useContext(ToastContext);
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<Contact[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    debounce(async () => {
      if (inputValue.length > 2) {
        fetchSuggesitons(inputValue);
      }
    }, 400)();
  }, [inputValue]);

  const fetchSuggesitons = async (inputValue: string) => {
    setLoading(true);
    let searchType: "name" | "phone" = "name";
    if (startsWithNumber(inputValue)) {
      inputValue = inputValue.replace(/\D/g, "");
      searchType = "phone";
    }
    try {
      const data = await getTenantContacts({
        tenantID: `${tenantID}`,
        search: inputValue,
        type: searchType,
      });
      if (data.length > 1) {
        setOptions(data.slice(0, 1));
      }
      setOptions(data);
    } catch (e) {
      const error = e as Error;
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t("fetchError", {
          name: t("contact.label_plural", { ns: "entities" }),
          status: error.message,
        }),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOnChange = (e: SyntheticEvent, value: Contact | null) => {
    setContact(value);
    setInputValue("");
  };

  const handleOnInputChange = (e: SyntheticEvent, value: string) => {
    setInputValue(value);
  };

  // MUI didn't type the props for renderOption so we have to use any
  const renderOption = useCallback((props: any, option: Contact) => {
    return (
      <Stack
        direction={"row"}
        spacing={1}
        p={2}
        onClick={props.onClick}
        sx={{
          cursor: "default",
          "&:hover": {
            backgroundColor: "whitesmoke",
          },
        }}
      >
        <Typography variant={"body2"} noWrap fontWeight={"bold"}>
          {`${option.name}`}
        </Typography>
        <Typography variant={"body2"} noWrap>
          {`${capitalize(option.description || "")}  ${formatPhoneNumber(
            option.primaryPhone || "",
          )}`}
        </Typography>
      </Stack>
    );
  }, []);

  return (
    <Autocomplete
      sx={{ bgcolor: "white", ...sx }}
      size={size}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
        setInputValue("");
      }}
      options={options}
      value={null}
      disabled={disabled}
      blurOnSelect
      aria-label={label}
      filterSelectedOptions
      isOptionEqualToValue={(option, value) => {
        return option.id === value.id;
      }}
      getOptionLabel={(option) => {
        return `Name: ${option.name} - Description: ${
          capitalize(option.description || "") || ""
        } - ${formatPhoneNumber(option.primaryPhone || "")}`;
      }}
      noOptionsText={t("noOptions", {
        name: t("contact.label_plural", { ns: "entities" }),
      })}
      loading={loading}
      loadingText={t("searching")}
      filterOptions={(x) => x}
      onInputChange={handleOnInputChange}
      onChange={handleOnChange}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={label}
          variant={variant}
          margin={margin}
          fullWidth
          InputLabelProps={{ shrink: false }}
          InputProps={{
            ...params.InputProps,
            sx: { px: 2 },
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={renderOption}
    />
  );
};

export default ContactAutocomplete;
